import { isEqual, pick } from "lodash";
import objectHash from "object-hash";
import { z } from "zod";

export const ExternalSourceHash = z.string().brand<"ExternalSourceHash">();
export type ExternalSourceHash = z.infer<typeof ExternalSourceHash>;

export const DEFAULT_UNKNOWN_EXTERNAL_SOURCE_ID = "unknown" as const;
export const DEFAULT_GITHUB_BASE_URL = "https://github.com" as const;

export const DEFAULT_UNKNOWN_EXTERNAL_CONTEXT_SOURCE: UnknownExternalContextSource =
  {
    id: DEFAULT_UNKNOWN_EXTERNAL_SOURCE_ID,
    source: "unknown",
  };

const BaseUrl = z.string().refine(
  (url) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  },
  { message: "Must be a valid URL" },
);

export type BaseUrl = z.infer<typeof BaseUrl>;

export const GithubExternalSourceLocator = z.object({
  source: z.literal("github"),
  base: BaseUrl, // usually `https://github.com` unless this is a self-hosted instance
  owner: z.string(),
  repo: z.string(),
});
export type GithubExternalSourceLocator = z.infer<
  typeof GithubExternalSourceLocator
>;

export const GithubExternalSourceLocatorWithMetadata =
  GithubExternalSourceLocator.extend({
    commitHash: z.string(),
    branch: z.string(),
  });
export type GithubExternalSourceLocatorWithMetadata = z.infer<
  typeof GithubExternalSourceLocatorWithMetadata
>;

export const GithubExternalContextSource =
  GithubExternalSourceLocatorWithMetadata.extend({
    path: z.string(),
  });
export type GithubExternalContextSource = z.infer<
  typeof GithubExternalContextSource
>;

export const UnknownExternalSourceLocator = z.object({
  source: z.literal("unknown"),
  id: z.string(),
});
export type UnknownExternalSourceLocator = z.infer<
  typeof UnknownExternalSourceLocator
>;

export const UnknownExternalSourceLocatorWithMetadata =
  UnknownExternalSourceLocator;
export type UnknownExternalSourceLocatorWithMetadata = z.infer<
  typeof UnknownExternalSourceLocatorWithMetadata
>;

export const UnknownExternalContextSource = UnknownExternalSourceLocator;
export type UnknownExternalContextSource = z.infer<
  typeof UnknownExternalContextSource
>;

/**
 * ExternalSourceLocator serves as a unique identifier for an external source.
 * This should only include the minimal information needed to identify the source as we use this to determine which keys should be used to generate an object hash
 */
export const ExternalSourceLocator = z.discriminatedUnion("source", [
  GithubExternalSourceLocator,
  UnknownExternalSourceLocator,
]);
export type ExternalSourceLocator = z.infer<typeof ExternalSourceLocator>;

/**
 * ExternalSourceLocatorWithMetadata includes metadata that can change throughout the lifetime of an external connection.
 * This can include things like commit hash and branch which we expect could change over time
 */
export const ExternalSourceLocatorWithMetadata = z.discriminatedUnion(
  "source",
  [
    GithubExternalSourceLocatorWithMetadata,
    UnknownExternalSourceLocatorWithMetadata,
  ],
);
export type ExternalSourceLocatorWithMetadata = z.infer<
  typeof ExternalSourceLocatorWithMetadata
>;

/**
 * ExternalContextSource is the full source object that includes all the metadata for an external source (e.g. an OrgGuideFile)
 */
export const ExternalContextSource = z.discriminatedUnion("source", [
  GithubExternalContextSource,
  UnknownExternalContextSource,
]);
export type ExternalContextSource = z.infer<typeof ExternalContextSource>;

export const externalSourceToLocatorSchemaMap = {
  github: GithubExternalSourceLocator,
  unknown: UnknownExternalSourceLocator,
} as const;

export const getGithubRepoUri = (
  source: GithubExternalSourceLocator,
): string => {
  const url = new URL(source.base);
  url.pathname = `/${source.owner}/${source.repo}`;
  return url.toString();
};

export const getGithubFileUri = (
  source: GithubExternalContextSource,
): string => {
  // e.g. https://github.com/hex-inc/hex/blob/1b088729d0a6ef9d8b2429560f4a54da53a29cc3/<path_to_file>
  const { base, commitHash, owner, path, repo } = source;
  const url = new URL(base);
  url.pathname = `/${owner}/${repo}/blob/${commitHash}/${path}`;
  return url.toString();
};

export const getExternalSourceUri = (
  source: ExternalContextSource,
): string | null => {
  if (hasExternalSourceLink(source)) {
    return getGithubFileUri(source);
  }
  return null;
};

export const hasExternalSourceLink = <T extends ExternalSourceLocator>(
  externalSource: T | null,
): externalSource is Extract<T, { source: "github" }> => {
  if (externalSource == null) {
    return false;
  }
  return externalSource.source === "github";
};

export const getHumanReadableSourceType = (
  source: ExternalContextSource | null,
): string => {
  if (source == null) {
    return "Hex";
  } else if (source.source === "github") {
    return `GitHub (${source.owner}/${source.repo})`;
  }
  return "API";
};

export const getShortNameForExternalSource = (
  source: ExternalContextSource | null,
): string => {
  if (source == null) {
    return "Hex";
  } else if (source.source === "github") {
    return `${source.repo}`;
  }
  return "API";
};

export const getDescriptorForLocatorWithMetadata = (
  source: ExternalSourceLocatorWithMetadata,
): string => {
  if (source.source === "github") {
    return `${source.repo}@${source.branch}`;
  }
  return "API";
};

export const getReadOnlyTagForExternalSource = (
  source: ExternalContextSource | null,
): string => {
  return source != null ? " (read-only)" : "";
};

/**
 * This function should always return the same value for external sources that are the same (e.g. github: owner, repo, and baseURL) - this is stored in database tables and used to dedupe external sources.
 * If changing this function's behavior, you will need to backfill database columns.
 */
export const getUniqueIdentifierForExternalSource = (
  source: ExternalSourceLocator | null,
): ExternalSourceHash => {
  if (source == null) {
    return ExternalSourceHash.parse("hex");
  } else {
    const locatorSchema = externalSourceToLocatorSchemaMap[source.source];
    const expectedKeys = Object.keys(locatorSchema.shape);
    const sourceValues = pick(source, expectedKeys);
    const hash = objectHash(sourceValues, {
      algorithm: "md5",
      unorderedObjects: true,
      respectType: false,
    });
    return ExternalSourceHash.parse(`external:${hash}`);
  }
};

export const checkIfIncomingExternalSourceCanWrite = (
  source: ExternalSourceLocator | null,
  incoming: ExternalSourceLocator | null,
): boolean => {
  // If the incoming source is hex authored, we can _only_ write to hex authored sources
  if (incoming == null) {
    return source == null;
  }
  // This is a hex file, so we cannot write to this
  if (source == null) {
    return false;
  }

  if (source.source === incoming.source) {
    const locatorSchema = externalSourceToLocatorSchemaMap[source.source];
    const expectedKeys = Object.keys(locatorSchema.shape);

    const sourceValues = pick(source, expectedKeys);
    const incomingValues = pick(incoming, expectedKeys);
    return isEqual(sourceValues, incomingValues);
  } else if (source.source === "unknown") {
    return true;
  }

  return false;
};

const SOURCE_ORDER = [null, "github", "unknown"] as const;

export const externalSourceComparator = (
  a: ExternalSourceLocator | null,
  b: ExternalSourceLocator | null,
): number => {
  const getSourceIndex = (source: ExternalSourceLocator | null): number => {
    const unadjusted = SOURCE_ORDER.indexOf(source ? source.source : null);
    // If not found, we put this at the end of the list
    return unadjusted === -1 ? SOURCE_ORDER.length : unadjusted;
  };
  const sourceAIndex = getSourceIndex(a);
  const sourceBIndex = getSourceIndex(b);

  if (sourceAIndex !== sourceBIndex) {
    return sourceAIndex - sourceBIndex;
  }

  if (a?.source === "github" && b?.source === "github") {
    const ownerCompare = a.owner.localeCompare(b.owner);
    if (ownerCompare !== 0) {
      return ownerCompare;
    }
    return a.repo.localeCompare(b.repo);
  } else {
    const aId = getUniqueIdentifierForExternalSource(a);
    const bId = getUniqueIdentifierForExternalSource(b);
    return aId.localeCompare(bId);
  }
};
